import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import {
  AppBar as MUIAppBar,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { AccountMenu } from '~/components/global/AccountMenu';
import {
  APP_BAR_HEIGHT,
  publishAppBarHeight,
  publishHeaderBlockHeight,
  resetAppBarHeight,
  resetHeaderBlockHeight,
} from '~/components/global/appBarHeight';
import { OttoHeaderButton } from '~/components/otto/OttoHeaderButton';
import { useOttoModeOrClosed } from '~/components/otto/OttoProvider';
import UploadDocumentButton from '~/components/uploadDocument/UploadDocumentButton';
import { WhatsNewBell } from '~/components/whatsNew/WhatsNewBell';
import { useGlobalFilters } from '~/hooks/useGlobalFilters';
import { BUTTON_SX, FILTER_CONTROL_SX } from '~/theme';
import { canAccess, UNIVERSAL_SEARCH, UPLOAD_DOCUMENT_FORM } from '~/utils/role';

const useIsFetching = () => {
  const { state } = useNavigation();
  // TODO Also check SWR for any of its in-progress loaders.
  return state === 'loading';
};

// Publish a measured height while this AppBar is mounted, and reset on unmount. Read
// for the fixed bar (`useAppBarHeight`) and for the whole header block, bar plus filter
// band (`useHeaderBlockHeight`). Routes that render no AppBar leave the stores at their
// defaults.
const usePublishedHeight = (publish: (height: number) => void, reset: () => void) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    publish(element.clientHeight);
    const resizeObserver = new ResizeObserver(() => {
      publish(element.clientHeight);
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      reset();
    };
  }, [publish, reset]);

  return ref;
};

interface AppBarProps {
  openSearch: () => void;
  // Opens the mobile navigation drawer. Only wired up below the `sm`
  // breakpoint, where the permanent sidebar is hidden.
  onMenuClick?: () => void;
}

export default function AppBar({ openSearch, onMenuClick }: AppBarProps) {
  const {
    userGroup,
    userDetails: { role: userRole },
  } = useContext(UserContext);
  const isFetching = useIsFetching();
  const [searchLabel] = useState(() => {
    if (typeof window === 'undefined') return 'Search';
    const modifier = window.navigator.platform.toLocaleLowerCase().startsWith('mac') ? '⌘' : 'ctrl';
    return `Search (${modifier} + K)`;
  });
  const { filters, chips, clearAll, canClear } = useGlobalFilters();
  const ottoMode = useOttoModeOrClosed();
  const showFilters = filters.length > 0 && ottoMode !== 'full';

  // Below `sm` (phones) the permanent sidebar is replaced by the hamburger, so
  // the AppBar switches to a compact single row: search collapses to an icon and
  // the secondary actions move into an overflow menu.
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const closeOverflow = () => setOverflowAnchor(null);

  const appBarRef = usePublishedHeight(publishAppBarHeight, resetAppBarHeight);
  // The band below the bar: the spacer reserving the fixed bar's height plus the
  // filter row and its chips, so this is the full height of the app's header.
  const headerBlockRef = usePublishedHeight(publishHeaderBlockHeight, resetHeaderBlockHeight);

  return (
    <>
      <MUIAppBar
        ref={appBarRef}
        sx={{
          backgroundColor: 'white',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: 'inherit',
        }}
        elevation={1}
      >
        <LinearProgress
          sx={{
            position: 'absolute',
            bottom: -2,
            width: '100%',
            display: isFetching ? 'block' : 'none',
          }}
          color="info"
        />

        <Toolbar
          variant="dense"
          sx={{
            flexWrap: 'nowrap',
            minHeight: APP_BAR_HEIGHT,
            py: 0.5,
            gap: 1,
          }}
        >
          {/* Mobile-only hamburger that opens the navigation drawer (the
              permanent sidebar is hidden below `sm`). */}
          {onMenuClick ? (
            <IconButton
              aria-label="Open navigation menu"
              onClick={onMenuClick}
              edge="start"
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}

          {/* Consultant company logo — hidden on phones to keep the row compact. */}
          {userGroup?.file ? (
            <Box sx={{ pr: 1.5, display: { xs: 'none', md: 'block' }, flexShrink: 0 }}>
              <img src={userGroup.file} alt="Consultant Company" style={{ height: '28px' }} />
            </Box>
          ) : null}

          {canAccess(userRole, UNIVERSAL_SEARCH) ? (
            isCompact ? (
              <IconButton type="button" aria-label={searchLabel} onClick={openSearch}>
                <SearchIcon />
              </IconButton>
            ) : (
              // A button, not the read-only InputBase this used to be: it never accepted typing —
              // it opens the command palette — and as an input it was a tab stop that did nothing.
              <Box
                component="button"
                type="button"
                aria-label={searchLabel}
                onClick={openSearch}
                sx={{
                  ...FILTER_CONTROL_SX,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  gap: 1,
                  px: 1.5,
                  minWidth: 200,
                  cursor: 'pointer',
                  color: 'text.secondary',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
              >
                <SearchIcon fontSize="small" />
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  {searchLabel}
                </Box>
              </Box>
            )
          ) : null}

          {/* Spacer pushes actions to the right on all breakpoints. */}
          <Box sx={{ flexGrow: 1 }} />

          {isCompact ? (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
              {/* Both stay in the bar on phones: the rail that holds Upload is behind
                  the hamburger there. */}
              <OttoHeaderButton />
              {canAccess(userRole, UPLOAD_DOCUMENT_FORM) ? (
                <UploadDocumentButton variant="compact" />
              ) : null}
              <AccountMenu />
              <IconButton
                aria-label="More actions"
                aria-haspopup="true"
                onClick={(e) => setOverflowAnchor(e.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
              <Popover
                open={Boolean(overflowAnchor)}
                anchorEl={overflowAnchor}
                onClose={closeOverflow}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Stack spacing={0.25} sx={{ p: 1, alignItems: 'stretch', minWidth: 200 }}>
                  {/* What's New opens its own popover anchored to its button, so
                      it must NOT close the overflow (that would unmount its
                      anchor). */}
                  <WhatsNewBell orientation="horizontal" />
                </Stack>
              </Popover>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
              <WhatsNewBell />
              <OttoHeaderButton />
              <AccountMenu />
            </Box>
          )}
        </Toolbar>
      </MUIAppBar>
      <Box
        ref={headerBlockRef}
        sx={{
          px: { xs: 2, md: 2 },
          // The spacer alone reserves exactly the fixed AppBar's height, so when
          // there are no global filters to show this band has nothing to pad —
          // the pt would just be dead `#F9FAFB` space between the bar and the
          // page (most visible on filter-less pages like Insights). Only add the
          // breathing room above the filter row when filters are actually shown.
          pt: showFilters ? { xs: 0.5, md: 1 } : 0,
          pb: 0,
          backgroundColor: '#F9FAFB',
        }}
      >
        {/* Reserves the fixed bar's height, from the same constant the Toolbar uses. */}
        <Box sx={{ height: APP_BAR_HEIGHT }} />
        {showFilters ? (
          <Box
            sx={{
              pt: 0.25,
              pb: 0,
            }}
          >
            <Grid
              container
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
            >
              {filters.map(({ name, component }) => (
                <Grid
                  key={name}
                  // On phones each filter gets its own full-width row and the
                  // control (fixed-width dropdown / date button) stretches to
                  // fill it. From `sm` up they sit inline at their natural widths.
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    '& .MuiFormControl-root': { width: { xs: '100%', sm: 200 } },
                    '& .MuiButtonBase-root': { width: { xs: '100%', sm: 'auto' } },
                  }}
                >
                  {component}
                </Grid>
              ))}
              {/* Only rendered once a dropdown is narrowing the page — Period always holds a range,
                  so it doesn't count as applied. */}
              {canClear ? (
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Button
                    size="small"
                    onClick={clearAll}
                    startIcon={<CloseIcon />}
                    sx={{ ...BUTTON_SX, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Clear all
                  </Button>
                </Grid>
              ) : null}
            </Grid>

            {/* What is actually applied, spelled out: the dropdowns collapse a multi-select to
                "3 selected"-style text. */}
            {chips.length > 0 ? (
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                gap={0.75}
                sx={{ mt: 0.75, mb: 0.25 }}
              >
                {chips.map(({ key, groupLabel, label, onDelete }) => (
                  <Chip
                    key={key}
                    label={
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
                      >
                        <Box
                          component="span"
                          sx={{ fontSize: '10px', fontWeight: 400, color: 'text.secondary' }}
                        >
                          {groupLabel}
                        </Box>
                        <Box component="span">{label}</Box>
                      </Box>
                    }
                    size="small"
                    onDelete={onDelete}
                    deleteIcon={<CancelIcon aria-label={`Remove ${groupLabel} ${label}`} />}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: 'primary.main',
                        opacity: 0.7,
                        '&:hover': { color: 'primary.main', opacity: 1 },
                      },
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </>
  );
}
